import Checkbox, { type CheckboxChangeEvent, type CheckboxProps } from "antd/lib/checkbox";
import { PureComponent, type ReactNode } from "react";

import { Hotkey } from "@btnet/core-components/Hotkey";
import {
    type IHotkeyWithPopoverConfig,
    type THotkeyCommands,
} from "@btnet/core-components/Hotkey/hotkey.types";
import { getHotkeyProps } from "@btnet/core-components/Hotkey/hotkey.utility";
import { cn } from "@btnet/utilities-core";

import { type ITrackingProp, track } from "utilities/analytics/analytics";
import { type KeyOfOrString } from "utilities/type/PropsOfType";

import "./BTCheckbox.less";

export interface IBTCheckboxProps<FormValues> extends Omit<CheckboxProps, "id"> {
    id: KeyOfOrString<FormValues>;
    "data-testid": string;
    children?: ReactNode;
    rounded?: boolean;
    readOnly?: boolean;
    hotkey?: THotkeyCommands | IHotkeyWithPopoverConfig;
    onHotkeyPressed?: (e: CheckboxChangeEvent) => void;
}

class BTCheckboxBase<FormValues> extends PureComponent<
    IBTCheckboxProps<FormValues> & ITrackingProp
> {
    private handleChange = (e: CheckboxChangeEvent) => {
        const { onChange, disabled } = this.props;
        if (disabled) {
            return;
        }

        if (onChange) {
            onChange(e);
        }
        this.props.tracking?.trackEvent({
            event: "ValueChange",
            extraInfo: {
                checked: e.target.checked,
            },
        });
    };

    // Use this if you want to pass a different function in from the normal checkbox
    private handleHotkeyPressed = (e: CheckboxChangeEvent) => {
        const { onHotkeyPressed, disabled } = this.props;
        if (disabled) {
            return;
        }

        if (onHotkeyPressed) {
            onHotkeyPressed(e);
        }
        this.props.tracking?.trackEvent({
            event: "ValueChange",
            extraInfo: {
                checked: e.target.checked,
            },
        });
    };

    render() {
        const {
            id,
            children,
            className,
            rounded,
            readOnly,
            disabled,
            tabIndex,
            hotkey,
            onHotkeyPressed,
            ...otherProps
        } = this.props;
        const checkClass = cn(className, {
            rounded: rounded,
            readOnly: readOnly,
            disabled: disabled,
        });

        const checkbox = (
            // eslint-disable-next-line @buildertrend/enterprise-extras/no-deprecated-element
            <Checkbox
                {...otherProps}
                className={checkClass}
                id={id && id.toString()}
                onChange={this.handleChange}
                tabIndex={disabled ? -1 : tabIndex}
                aria-disabled={disabled}
            >
                {children}
            </Checkbox>
        );

        if (!hotkey) {
            return checkbox;
        }
        const checkboxChangeEvent: CheckboxChangeEvent = {
            target: {
                checked: true,
            },
            nativeEvent: new MouseEvent("submit"),
            stopPropagation: () => {},
            preventDefault: () => {},
        };
        return (
            <Hotkey
                {...getHotkeyProps(hotkey)}
                disabled={disabled}
                onCommand={() => {
                    if (onHotkeyPressed) {
                        this.handleHotkeyPressed(checkboxChangeEvent);
                    } else {
                        this.handleChange(checkboxChangeEvent);
                    }
                }}
            >
                {checkbox}
            </Hotkey>
        );
    }
}

export const BTCheckbox = track((props) => ({
    element: "Checkbox",
    uniqueId: props["data-testid"],
}))(BTCheckboxBase);
